import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import BulkCreateBrandProviderModal from '@modals/BulkCreateBrandProviderModal'
import CreateBrandGroupProviderModal from '@modals/CreateBrandGroupProviderModal'
import CreateBrandGroupPublicationModal from '@modals/CreateBrandGroupPublicationModal'
import CreateOrEditPublicationEditionModal from '@modals/CreateOrEditPublicationEditionModal'
import CreateOrEditPublicationOptionsModal from '@modals/CreateOrEditPublicationOptionsModal'
import CreateOrEditPublicationProductModal from '@modals/CreateOrEditPublicationProductModal'
import CreatePublicationCompatiblePaginationModal from '@modals/CreatePublicationCompatiblePaginationModal'
import CreatePublicationPaginationRuleModal from '@modals/CreatePublicationPaginationRuleModal'
import CreatePublicationSpecialDeadlineModal from '@modals/CreatePublicationSpecialDeadlineModal'
import EditPublicationDeliverySettingsModal from '@modals/EditPublicationDeliverySettingsModal'
import EditPublicationFileTransferSettingsModal from '@modals/EditPublicationFileTransferSettingsModal'
import ManagePublicationDeadlinesModal from '@modals/ManagePublicationDeadlinesModal'
import ManagePublicationPrintSpecificationsModal from '@modals/ManagePublicationPrintSpecificationsModal'

import PageContent from './components/PageContent'

const callbacks = (component, setState) => {
  const componentCallbacks = {
    BulkCreateBrandProviderModal: {
      closeModal: () => setState({ showBulkCreateBrandProviderModal: false }),
      bulkCreateBrandProviders: payload => handleCallbackAction(payload),
    },
    CreateBrandGroupProviderModal: {
      closeModal: () => setState({ showCreateBrandGroupProviderModal: false }),
      createBrandGroupProvider: payload => (
        handleCallbackAction({
          ...payload,
          requestOptions: {
            ...payload.requestOptions,
            include_brand_group_provider_brand_group: true,
            include_brand_group_provider_ids: true,
          },
        })
      ),
    },
    CreateBrandGroupPublicationModal: {
      closeModal: () => setState({ showCreateBrandGroupPublicationModal: false }),
      createBrandGroupPublication: payload => handleCallbackAction(payload),
    },
    CreateOrEditPublicationEditionModal: {
      closeModal: () => setState({ showCreateOrEditPublicationEditionModal: false }),
      createPublicationEdition: payload => handleCallbackAction(payload),
      updatePublicationEdition: payload => handleCallbackAction(payload),
    },
    CreateOrEditPublicationOptionsModal: {
      closeModal: () => setState({ showCreateOrEditPublicationOptionsModal: false }),
      createPublicationOption: payload => handleCallbackAction(payload),
      deletePublicationOption: payload => handleCallbackAction(payload),
      updatePublicationOption: payload => handleCallbackAction(payload),
    },
    CreateOrEditPublicationProductModal: {
      closeModal: () => setState({ showCreateOrEditPublicationProductModal: false }),
      createPublicationProduct: payload => handleCallbackAction(payload),
      updatePublicationProduct: payload => handleCallbackAction(payload),
    },
    CreatePublicationCompatiblePaginationModal: {
      closeModal: () => setState({ showCreatePublicationCompatiblePaginationModal: false }),
      createPublicationCompatiblePagination: payload => handleCallbackAction(payload),
    },
    CreatePublicationPaginationRuleModal: {
      closeModal: () => setState({ showCreatePublicationPaginationRuleModal: false }),
      createPublicationPaginationRule: payload => handleCallbackAction(payload),
    },
    CreatePublicationSpecialDeadlineModal: {
      closeModal: () => setState({ showCreatePublicationSpecialDeadlineModal: false }),
      createPublicationSpecialDeadline: payload => handleCallbackAction(payload),
    },
    EditPublicationDeliverySettingsModal: {
      closeModal: () => setState({ showEditPublicationDeliverySettingsModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    EditPublicationFileTransferSettingsModal: {
      closeModal: () => setState({ showEditPublicationFileTransferSettingsModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    ManagePublicationDeadlinesModal: {
      closeModal: () => setState({ showManagePublicationDeadlinesModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
    ManagePublicationPrintSpecificationsModal: {
      closeModal: () => setState({ showManagePublicationPrintSpecificationsModal: false }),
      updatePublication: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showBulkCreateBrandProviderModal: false,
  showCreateBrandGroupProviderModal: false,
  showCreateBrandGroupPublicationModal: false,
  showCreateOrEditPublicationEditionModal: false,
  showCreateOrEditPublicationOptionsModal: false,
  showCreateOrEditPublicationProductModal: false,
  showCreatePublicationCompatiblePaginationModal: false,
  showCreatePublicationPaginationRuleModal: false,
  showCreatePublicationSpecialDeadlineModal: false,
  showEditPublicationDeliverySettingsModal: false,
  showEditPublicationFileTransferSettingsModal: false,
  showManagePublicationDeadlinesModal: false,
  showManagePublicationPrintSpecificationsModal: false,
}

const Relations = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateBrandProviderModal,
    showCreateBrandGroupProviderModal,
    showCreateBrandGroupPublicationModal,
    showCreateOrEditPublicationEditionModal,
    showCreateOrEditPublicationOptionsModal,
    showCreateOrEditPublicationProductModal,
    showCreatePublicationCompatiblePaginationModal,
    showCreatePublicationPaginationRuleModal,
    showCreatePublicationSpecialDeadlineModal,
    showEditPublicationDeliverySettingsModal,
    showEditPublicationFileTransferSettingsModal,
    showManagePublicationDeadlinesModal,
    showManagePublicationPrintSpecificationsModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const { publicationId } = useNumericParams()

  const pageContext = useMemo(() => ({
    callbacks: {
      createPublicationOptionValue: payload => handleCallbackAction(payload),
      deleteCompatiblePagination: payload => handleCallbackAction(payload),
      deleteOption: payload => handleCallbackAction(payload),
      deletePublicationEdition: payload => handleCallbackAction(payload),
      deletePublicationOptionValue: payload => handleCallbackAction(payload),
      deletePublicationPaginationRule: payload => handleCallbackAction(payload),
      deleteSpecialDeadline: payload => handleCallbackAction(payload),
      showBulkCreateBrandProviderModal: (payload) => {
        setModalData('BulkCreateBrandProviderModal', payload)
        setState({ showBulkCreateBrandProviderModal: true })
      },
      showCreateBrandGroupProviderModal: (payload) => {
        setModalData('CreateBrandGroupProviderModal', payload)
        setState({ showCreateBrandGroupProviderModal: true })
      },
      showCreateBrandGroupPublicationModal: (payload) => {
        setModalData('CreateBrandGroupPublicationModal', payload)
        setState({ showCreateBrandGroupPublicationModal: true })
      },
      showCreateOrEditPublicationEditionModal: (payload) => {
        setModalData('CreateOrEditPublicationEditionModal', payload)
        setState({ showCreateOrEditPublicationEditionModal: true })
      },
      showCreateOrEditPublicationOptionsModal: (payload) => {
        setModalData('CreateOrEditPublicationOptionsModal', payload)
        setState({ showCreateOrEditPublicationOptionsModal: true })
      },
      showCreateOrEditPublicationProductModal: (payload) => {
        setModalData('CreateOrEditPublicationProductModal', payload)
        setState({ showCreateOrEditPublicationProductModal: true })
      },
      showCreatePublicationCompatiblePaginationModal: (payload) => {
        setModalData('CreatePublicationCompatiblePaginationModal', payload)
        setState({ showCreatePublicationCompatiblePaginationModal: true })
      },
      showCreatePublicationPaginationRuleModal: (payload) => {
        setModalData('CreatePublicationPaginationRuleModal', payload)
        setState({ showCreatePublicationPaginationRuleModal: true })
      },
      showCreatePublicationSpecialDeadlineModal: (payload) => {
        setModalData('CreatePublicationSpecialDeadlineModal', payload)
        setState({ showCreatePublicationSpecialDeadlineModal: true })
      },
      showEditPublicationDeliverySettingsModal: (payload) => {
        setModalData('EditPublicationDeliverySettingsModal', payload)
        setState({ showEditPublicationDeliverySettingsModal: true })
      },
      showEditPublicationFileTransferSettingsModal: (payload) => {
        setModalData('EditPublicationFileTransferSettingsModal', payload)
        setState({ showEditPublicationFileTransferSettingsModal: true })
      },
      showManagePublicationDeadlinesModal: (payload) => {
        setModalData('ManagePublicationDeadlinesModal', payload)
        setState({ showManagePublicationDeadlinesModal: true })
      },
      showManagePublicationPrintSpecificationsModal: (payload) => {
        setModalData('ManagePublicationPrintSpecificationsModal', payload)
        setState({ showManagePublicationPrintSpecificationsModal: true })
      },
      updatePublication: payload => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent publicationId={publicationId} />

        <BulkCreateBrandProviderModal
          callbacks={callbacks('BulkCreateBrandProviderModal', setState)}
          showModal={showBulkCreateBrandProviderModal}
        />

        <CreateBrandGroupProviderModal
          callbacks={callbacks('CreateBrandGroupProviderModal', setState)}
          showModal={showCreateBrandGroupProviderModal}
        />

        <CreateBrandGroupPublicationModal
          callbacks={callbacks('CreateBrandGroupPublicationModal', setState)}
          showModal={showCreateBrandGroupPublicationModal}
        />

        <CreateOrEditPublicationEditionModal
          callbacks={callbacks('CreateOrEditPublicationEditionModal', setState)}
          showModal={showCreateOrEditPublicationEditionModal}
        />

        <CreateOrEditPublicationOptionsModal
          callbacks={callbacks('CreateOrEditPublicationOptionsModal', setState)}
          showModal={showCreateOrEditPublicationOptionsModal}
        />

        <CreateOrEditPublicationProductModal
          callbacks={callbacks('CreateOrEditPublicationProductModal', setState)}
          showModal={showCreateOrEditPublicationProductModal}
        />

        <CreatePublicationCompatiblePaginationModal
          callbacks={callbacks('CreatePublicationCompatiblePaginationModal', setState)}
          showModal={showCreatePublicationCompatiblePaginationModal}
        />

        <CreatePublicationPaginationRuleModal
          callbacks={callbacks('CreatePublicationPaginationRuleModal', setState)}
          showModal={showCreatePublicationPaginationRuleModal}
        />

        <CreatePublicationSpecialDeadlineModal
          callbacks={callbacks('CreatePublicationSpecialDeadlineModal', setState)}
          showModal={showCreatePublicationSpecialDeadlineModal}
        />

        <EditPublicationDeliverySettingsModal
          callbacks={callbacks('EditPublicationDeliverySettingsModal', setState)}
          showModal={showEditPublicationDeliverySettingsModal}
        />

        <EditPublicationFileTransferSettingsModal
          callbacks={callbacks('EditPublicationFileTransferSettingsModal', setState)}
          showModal={showEditPublicationFileTransferSettingsModal}
        />

        <ManagePublicationDeadlinesModal
          callbacks={callbacks('ManagePublicationDeadlinesModal', setState)}
          showModal={showManagePublicationDeadlinesModal}
        />

        <ManagePublicationPrintSpecificationsModal
          callbacks={callbacks('ManagePublicationPrintSpecificationsModal', setState)}
          showModal={showManagePublicationPrintSpecificationsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
